import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { StorefrontProduct } from '../../api/client';
import { useShopNames } from '../../commerce/shopNames';
import { useShopWords } from '../../commerce/shopWords';
import { trapTab, useDialogFocus, useScrollLock } from '../../components/CheckoutModal';
import { useProduct, useProductVariant } from './productContext';
import { ProductScope } from './ProductScope';
import { railCount, railFaces } from './railGarment';
import { responsive } from './responsiveImage';
import { useIsoLayoutEffect } from './useIsoLayoutEffect';
import { asksForStillness } from './useRailMotion';

// The garment that came forward: it flies from its place on the rail to the middle of the window as the page behind it
// blurs away, turns between its front and its back, and steps along to the rail's other garments. A real dialog: focus
// moves in and stays in, Escape closes it, the arrow keys step, and focus goes back to the garment it was opened from.

/** How long the flight forward takes, and the curve it settles on, measured off the reference at about two thirds of a second. */
const FLY_MS = 620;
const FLY_EASE = 'cubic-bezier(0.2, 0.75, 0.2, 1)';

/** How long the flight back takes, a little quicker than the way out. */
const FLY_BACK_MS = 460;

/** How long a garment takes to turn to its edge and how long to open from it. */
const TURN_OUT_MS = 200;
const TURN_IN_MS = 300;

/** What the dialog draws: the rail's garments, which one, which views they have, where it flew from, and how to move and close. */
type DetailProps = {
  rows: readonly StorefrontProduct[]; at: number; sourceOf: (at: number) => HTMLElement | null;
  onMove: (at: number) => void; onClose: () => void; children: React.ReactNode;
};

// Writes the transform that puts the stage exactly over its place on the rail, scaled from the stage's top left.
function overRail(stage: HTMLElement, source: HTMLElement | null): string | null {
  const to = stage.getBoundingClientRect();
  const from = source?.getBoundingClientRect();
  if (!from || !from.width || !to.width) return null;
  return `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})`;
}

// Turns a picture from facing the shopper to its edge, or from its edge to facing them.
const turnFace = (face: Element | null | undefined, opening: boolean): Promise<unknown> | undefined => {
  if (!face || asksForStillness()) return undefined;
  const frames = [{ transform: 'perspective(1400px) rotateY(0deg)' }, { transform: `perspective(1400px) rotateY(${opening ? 90 : -90}deg)` }];
  return face.animate(opening ? frames.reverse() : frames, { duration: opening ? TURN_IN_MS : TURN_OUT_MS, easing: opening ? 'cubic-bezier(0.15, 0.65, 0.3, 1)' : 'cubic-bezier(0.55, 0, 0.85, 0.35)', fill: opening ? 'none' : 'forwards' }).finished;
};

/** How wide a garment's picture shows when it has come forward. */
const STAGE_SIZES = '(max-width: 749px) 80vw, 640px';

// Fetches and decodes a picture ahead of showing it, so a garment never turns in from its edge with nothing to show; a picture that fails is left for the page to report.
const readied = (src: string): Promise<unknown> => {
  if (!src) return Promise.resolve();
  const image = new Image();
  const { srcSet, sizes } = responsive(src, STAGE_SIZES);
  if (sizes) image.sizes = sizes;
  if (srcSet) image.srcset = srcSet;
  image.src = src;
  return image.decode().catch(() => undefined);
};

// Draws the garment itself and its Front and Back, reading the option chosen in the panel under it; a colour with its own picture turns to that picture.
const RailStage: React.FC<{ stage: React.RefObject<HTMLDivElement>; arrived: boolean; count: string }> = ({ stage, arrived, count }) => {
  const product = useProduct();
  const { selected } = useProductVariant();
  const words = useShopWords();
  const [showing, setShowing] = useState<'front' | 'back'>('front');
  const chosen = product?.variants.find((variant) => variant.id === selected)?.imageId;
  const faces = product ? railFaces(product, chosen) : null;
  const picture = useRef<HTMLImageElement>(null);
  const face = showing === 'back' && faces?.back ? 'back' : 'front';
  const src = faces ? (face === 'back' ? faces.back : faces.front) : '';
  // A garment stepped to, a colour picked or a view chosen turns in from its edge; the first one has only just flown here.
  const first = useRef(!arrived);
  useIsoLayoutEffect(() => {
    if (first.current) { first.current = false; return; }
    void turnFace(picture.current, true);
  }, [src]);
  // Its other view is ready before anyone asks for it.
  useEffect(() => { if (faces?.back) void readied(faces.back); }, [faces?.back]);
  // Turns the garment to its edge, and shows the other view once both that and its picture are ready.
  const choose = (next: 'front' | 'back') => {
    if (next === face) return;
    void Promise.all([turnFace(picture.current, false), readied(next === 'back' ? faces?.back ?? '' : faces?.front ?? '')]).then(() => setShowing(next));
  };
  return <>
    <div className="xe-rail-stage" ref={stage}>
      {src ? <button type="button" className="xe-rail-stage-turn" aria-label={face === 'front' ? words.backView : words.frontView} disabled={!faces?.back} onClick={() => choose(face === 'front' ? 'back' : 'front')}>
        <img ref={picture} key={src} className="xe-rail-stage-face" {...responsive(src, STAGE_SIZES)} alt={product?.title ?? ''} decoding="sync" draggable={false} />
      </button> : null}
    </div>
    <div className="xe-rail-views">
      <span className="xe-rail-count">{count}</span>
      {faces?.back ? <span className="xe-rail-sides" role="group">
        {(['front', 'back'] as const).map((side) => <button key={side} type="button" aria-pressed={face === side} onClick={() => choose(side)}>{side === 'front' ? words.frontView : words.backView}</button>)}
      </span> : null}
    </div>
  </>;
};

// Keeps the page from moving sideways while its scrollbar is gone, so the rail behind stays where the garment flies back to.
function useSteadyPage(): void {
  useEffect(() => {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const previous = document.body.style.paddingRight;
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    return () => { document.body.style.paddingRight = previous; };
  }, []);
}

// Draws the dialog over the blurred page: the garment and its views, the merchant's elements under it, and the way out and along.
export const RailDetail: React.FC<DetailProps> = ({ rows, at, sourceOf, onMove, onClose, children }) => {
  const panel = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const names = useShopNames();
  const words = useShopWords();
  const [closing, setClosing] = useState(false);
  const stepped = useRef(false);
  const busy = useRef(false);
  useSteadyPage();
  useDialogFocus(true, panel);
  useScrollLock(true);
  // The garments either side are ready before a shopper steps to them.
  useEffect(() => { for (const by of [1, -1]) void readied(railFaces(rows[(at + by + rows.length) % rows.length], null).front); }, [at, rows]);
  // The flight forward, from the garment's place on the rail.
  useIsoLayoutEffect(() => {
    const from = stage.current ? overRail(stage.current, sourceOf(at)) : null;
    if (from && !asksForStillness()) stage.current?.animate([{ transform: from }, { transform: 'none' }], { duration: FLY_MS, easing: FLY_EASE });
  }, []);
  // Flies the garment back to its place on the rail, then closes.
  const leave = () => {
    if (closing) return;
    const to = stage.current ? overRail(stage.current, sourceOf(at)) : null;
    if (!to || asksForStillness()) return onClose();
    setClosing(true);
    const back = stage.current?.animate([{ transform: 'none' }, { transform: to }], { duration: FLY_BACK_MS, easing: FLY_EASE, fill: 'forwards' });
    if (back) back.onfinish = onClose; else onClose();
  };
  // Turns the garment to its edge, then steps along the rail, round from the last to the first.
  const step = (by: number) => {
    if (busy.current || rows.length < 2) return;
    busy.current = true;
    stepped.current = true;
    const next = (at + by + rows.length) % rows.length;
    void Promise.all([turnFace(stage.current?.querySelector('img'), false), readied(railFaces(rows[next], null).front)]).then(() => { busy.current = false; onMove(next); });
  };
  const keys = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') return leave();
    if (event.key === 'ArrowRight') return step(1);
    if (event.key === 'ArrowLeft') return step(-1);
    if (event.key === 'Tab') trapTab(event, panel.current);
  };
  const product = rows[at];
  return (
    <div className="xe-rail-detail" role="dialog" aria-modal="true" aria-label={product.title} tabIndex={-1} ref={panel} onKeyDown={keys} data-closing={closing || undefined}>
      <div className="xe-rail-shade" aria-hidden="true" onClick={leave} />
      <button type="button" className="xe-rail-close" aria-label={words.close} onClick={leave}><X size={22} aria-hidden="true" /></button>
      {rows.length > 1 ? <>
        <button type="button" className="xe-rail-step" data-side="start" aria-label={names.previous} onClick={() => step(-1)}><ChevronLeft size={22} aria-hidden="true" /></button>
        <button type="button" className="xe-rail-step" data-side="end" aria-label={names.next} onClick={() => step(1)}><ChevronRight size={22} aria-hidden="true" /></button>
      </> : null}
      <ProductScope key={product.id} product={product}>
        <RailStage stage={stage} arrived={stepped.current} count={railCount(at, rows.length)} />
        <div className="xe-rail-panel">{children}</div>
      </ProductScope>
    </div>
  );
};
